import { useEffect, useRef, useState } from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { ActionMenu } from "@/components/ActionMenu";
import { NoteComposer } from "@/components/NoteComposer";
import { PhoneLink } from "@/components/PhoneLink";
import { TextButton } from "@/components/TextButton";
import { useToast } from "@/components/Toast";
import { Button, inputClass } from "@/components/ui";
import { apiRequest } from "@/lib/api";
import {
  autoLabel,
  callLog,
  OUTCOME_CHIPS,
  OUTCOME_LABEL,
  OUTCOME_SHORT,
  outcomeClass,
  tomorrowTenLocal,
  type CallLogEntry,
  type CallLogResult,
  type CallOutcome,
  type CallOwner,
} from "@/lib/callLog";
import { formatDateTime, formatPhone } from "@/lib/format";
import { sms, TEXT_STATUS_LABEL } from "@/lib/sms";
import { formatDuration } from "@/lib/telephony";
import { formatClock, recordDial, VOICE_WINDOW, voiceDialUrl } from "@/lib/voice";
import { cn } from "@/lib/utils";

/**
 * v2.9 P1.2: the call log — the quick-log row and the entries, on a card (page and drawer) and a lead.
 *
 * One click on an outcome chip logs the call with the default phone and direction; the note box stays
 * open and "Save note" puts what is typed on the call just logged. The outcome schedules the next attempt
 * server-side (D-122). Nothing here places a call or changes a stage on its own: Dead is OFFERED after a
 * wrong number / not interested / do not call on a lead, and only Sam's click applies it (Rule 0).
 */

export function OutcomePill({
  outcome,
  children,
  testId,
}: {
  outcome: CallOutcome | null | undefined;
  children?: React.ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={cn("rounded-full border px-1.5 text-[10px] font-medium whitespace-nowrap", outcomeClass(outcome))}
      data-testid={testId}
      data-outcome={outcome ?? ""}
    >
      {children ?? (outcome ? OUTCOME_LABEL[outcome] : "Logged")}
    </span>
  );
}

const DEAD_LABEL: Record<string, string> = {
  wrong_number: "Wrong number",
  not_interested: "Not interested",
  unsubscribed: "Unsubscribed (do not call)",
};

export function QuickLog({
  owner,
  phones,
  onLogged,
  focusRef,
  compact = false,
  occurredAt,
  initialDirection,
  testId = "calllog-quick",
}: {
  owner: CallOwner;
  phones: readonly string[];
  onLogged: (result: CallLogResult) => void;
  /** Registered so the `L` hotkey can open the row from anywhere on the page. */
  focusRef?: React.MutableRefObject<(() => void) | null>;
  compact?: boolean;
  /** P3.5: logging the outcome of a call Voice already logged (the Inbox link dialog). */
  occurredAt?: string;
  initialDirection?: "out" | "in";
  testId?: string;
}) {
  const toast = useToast();
  const [direction, setDirection] = useState<"out" | "in">(initialDirection ?? "out");
  const [phone, setPhone] = useState<string>(phones[0] ?? "");
  const [other, setOther] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<{ id: string; outcome: CallOutcome } | null>(null);
  const [callback, setCallback] = useState<string | null>(null);
  const [deadOffer, setDeadOffer] = useState<string | null>(null);
  const [nextStep, setNextStep] = useState<{ text: string; due: string } | null>(null);
  const firstChip = useRef<HTMLButtonElement>(null);
  const noteBox = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!phone && phones[0]) setPhone(phones[0]);
  }, [phones, phone]);

  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = () => {
      firstChip.current?.scrollIntoView({ block: "nearest" });
      firstChip.current?.focus();
    };
    return () => {
      if (focusRef) focusRef.current = null;
    };
  }, [focusRef]);

  const chosenPhone = phone === "__other" ? other.trim() : phone;

  const log = async (outcome: CallOutcome, callbackAt?: string) => {
    setBusy(true);
    try {
      const result = await callLog.log(owner, {
        channel: "call",
        direction,
        outcome,
        note: note.trim() || null,
        phone_e164: chosenPhone || null,
        callback_at: callbackAt ? new Date(callbackAt).toISOString() : null,
        occurred_at: occurredAt ?? null,
      });
      const next = result.next_attempt_at
        ? ` · next try ${new Date(result.next_attempt_at).toLocaleString("en-US", {
            timeZone: "America/New_York",
            weekday: "short",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}`
        : "";
      toast.push(`Logged: ${OUTCOME_LABEL[outcome]}${result.merged_with_voice ? " (on the Voice call)" : ""}${next}`);
      setLast({ id: result.entry.id, outcome });
      setNote("");
      setCallback(null);
      setDeadOffer(result.offer_dead_reason ?? null);
      if (outcome === "connected") {
        setNextStep({ text: "", due: tomorrowTenLocal() });
        requestAnimationFrame(() => noteBox.current?.focus());
      } else {
        setNextStep(null);
      }
      onLogged(result);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    if (!last || !note.trim()) return;
    setBusy(true);
    try {
      const result = await callLog.update(last.id, { note: note.trim() });
      toast.push("Note saved on the call");
      setNote("");
      onLogged(result);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const saveNextStep = async () => {
    if (!nextStep?.text.trim()) return;
    const due = new Date(nextStep.due).toISOString();
    try {
      if (owner.kind === "deal") {
        await apiRequest(`/api/v1/curative/deals/${owner.id}/reminders`, {
          method: "POST",
          body: JSON.stringify({ title: nextStep.text.trim(), due_at: due, source: "manual" }),
        });
      } else {
        await apiRequest(`/api/v1/leads/${owner.id}`, {
          method: "PATCH",
          body: JSON.stringify({ next_action: nextStep.text.trim(), next_action_due: due }),
        });
      }
      toast.push("Next step set");
      setNextStep(null);
      if (last) onLogged({ entry: { id: last.id } } as CallLogResult);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    }
  };

  const markDead = async () => {
    if (!deadOffer) return;
    try {
      await apiRequest(`/api/v1/leads/${owner.id}`, {
        method: "PATCH",
        body: JSON.stringify({ stage: "dead", dead_reason: deadOffer, next_action: null, next_action_due: null }),
      });
      toast.push(`Marked Dead — ${DEAD_LABEL[deadOffer] ?? deadOffer}`);
      setDeadOffer(null);
      if (last) onLogged({ entry: { id: last.id } } as CallLogResult);
    } catch (e) {
      toast.push((e as Error).message, "danger");
    }
  };

  return (
    <div className="space-y-1.5 text-xs" data-testid={testId}>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Log a call: pick the outcome">
        {OUTCOME_CHIPS.map((outcome, i) => (
          <button
            key={outcome}
            ref={i === 0 ? firstChip : undefined}
            type="button"
            disabled={busy}
            data-testid={`calllog-chip-${outcome}`}
            title={OUTCOME_LABEL[outcome]}
            className={cn(
              "rounded-full border font-medium hover:opacity-80 disabled:opacity-50",
              compact ? "px-1.5 text-[10px]" : "px-2 py-0.5",
              "pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-coarse:px-3 pointer-coarse:text-xs",
              outcomeClass(outcome),
            )}
            onClick={() => {
              if (outcome === "callback_requested") setCallback(tomorrowTenLocal());
              else void log(outcome);
            }}
          >
            {compact ? OUTCOME_SHORT[outcome] : OUTCOME_LABEL[outcome]}
          </button>
        ))}
      </div>
      {callback !== null && (
        <div className="flex flex-wrap items-center gap-2" data-testid="calllog-callback">
          <span className="text-fg-muted">Call back at</span>
          <input
            type="datetime-local"
            value={callback}
            onChange={(e) => setCallback(e.target.value)}
            className={cn(inputClass, "h-7 w-48 text-xs")}
            aria-label="Callback time"
            data-testid="calllog-callback-at"
          />
          <Button size="sm" variant="primary" disabled={busy} onClick={() => void log("callback_requested", callback)} data-testid="calllog-callback-save">
            Log callback
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCallback(null)}>
            Cancel
          </Button>
        </div>
      )}
      {!compact && (
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex rounded border" role="group" aria-label="Direction">
            {(["out", "in"] as const).map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={direction === d}
                data-testid={`calllog-direction-${d}`}
                className={cn("px-1.5 py-0.5", direction === d ? "bg-accent/15 text-accent" : "text-fg-muted")}
                onClick={() => setDirection(d)}
              >
                {d === "out" ? "Out" : "In"}
              </button>
            ))}
          </div>
          <select
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-label="Phone"
            data-testid="calllog-phone"
            className={cn(inputClass, "h-7 w-40 py-0 text-xs")}
          >
            {phones.map((p) => (
              <option key={p} value={p}>
                {formatPhone(p)}
              </option>
            ))}
            {phones.length === 0 && <option value="">No number on file</option>}
            <option value="__other">other…</option>
          </select>
          {phone === "__other" && (
            <input
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="(555) 555-0100"
              className={cn(inputClass, "h-7 w-36 text-xs")}
              aria-label="Other number"
              data-testid="calllog-phone-other"
            />
          )}
        </div>
      )}
      {!compact && (
        // v2.10 P5.1: the same composer as a note — Enter saves it on the call, Shift+Enter is a new line.
        <NoteComposer
          testId="calllog-note"
          ids={{ body: "calllog-note", submit: "calllog-note-save" }}
          inputRef={noteBox}
          value={note}
          onChange={setNote}
          canSubmit={!!last}
          busy={busy}
          submitLabel="Save note"
          onSubmit={() => saveNote()}
          placeholder={last ? `Add a note to that call (${OUTCOME_LABEL[last.outcome]})…` : "Note (optional) — then pick the outcome"}
        />
      )}
      {nextStep && !compact && (
        <div className="flex flex-wrap items-center gap-1.5 rounded border border-green-500/30 bg-green-500/5 p-1.5" data-testid="calllog-next-step">
          <span className="font-medium">Connected — next step?</span>
          <input
            value={nextStep.text}
            onChange={(e) => setNextStep({ ...nextStep, text: e.target.value })}
            placeholder="e.g. Send the offer"
            className={cn(inputClass, "h-7 min-w-40 flex-1 text-xs")}
            data-testid="calllog-next-step-text"
          />
          <input
            type="datetime-local"
            value={nextStep.due}
            onChange={(e) => setNextStep({ ...nextStep, due: e.target.value })}
            className={cn(inputClass, "h-7 w-48 text-xs")}
            aria-label="Next step due"
          />
          <Button size="sm" disabled={!nextStep.text.trim()} onClick={() => void saveNextStep()} data-testid="calllog-next-step-save">
            Set
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setNextStep(null)}>
            Skip
          </Button>
        </div>
      )}
      {deadOffer && owner.kind === "lead" && (
        <div className="flex flex-wrap items-center gap-1.5 rounded border border-danger/30 bg-danger/5 p-1.5" data-testid="calllog-dead-offer">
          <span>
            Mark this lead <strong>Dead — {DEAD_LABEL[deadOffer] ?? deadOffer}</strong>?
          </span>
          <Button size="sm" variant="danger" onClick={() => void markDead()} data-testid="calllog-dead-confirm">
            Mark dead
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeadOffer(null)} data-testid="calllog-dead-keep">
            Keep
          </Button>
        </div>
      )}
    </div>
  );
}

export function CallLogEntries({
  entries,
  canEdit,
  onChanged,
}: {
  entries: readonly CallLogEntry[];
  canEdit: (entry: CallLogEntry) => boolean;
  onChanged: () => void;
}) {
  if (entries.length === 0) {
    return <p className="text-xs text-fg-muted">No calls logged yet.</p>;
  }
  return (
    <ul className="space-y-1" data-testid="calllog-entries">
      {entries.map((entry) => (
        <CallLogRow key={entry.id} entry={entry} editable={canEdit(entry)} onChanged={onChanged} />
      ))}
    </ul>
  );
}

function CallLogRow({
  entry,
  editable,
  onChanged,
}: {
  entry: CallLogEntry;
  editable: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [draft, setDraft] = useState(entry.note ?? "");
  const run = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work();
      toast.push(done);
      onChanged();
    } catch (e) {
      toast.push((e as Error).message, "danger");
    }
  };
  const Arrow = entry.direction === "in" ? ArrowDownLeft : ArrowUpRight;
  const isText = entry.channel === "text";
  const auto = entry.outcome_source === "auto" && !entry.confirmed_at && !!entry.outcome;
  const [noteAfterConfirm, setNoteAfterConfirm] = useState(false);
  const owner: CallOwner | null = entry.deal_id
    ? { kind: "deal", id: entry.deal_id }
    : entry.lead_id
      ? { kind: "lead", id: entry.lead_id }
      : null;
  const missed = entry.direction === "in" && (entry.voice_kind === "missed" || entry.voice_kind === "voicemail");
  return (
    <li
      className={cn("rounded border px-2 py-1.5 text-xs", entry.provisional && "border-dashed")}
      data-testid="calllog-entry"
      data-entry-id={entry.id}
      data-provisional={entry.provisional ? "true" : "false"}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="tabular-nums text-fg-muted">{formatDateTime(entry.occurred_at)}</span>
        {isText ? (
          <span className="rounded-full border px-1.5 text-[10px] font-medium" data-testid="calllog-text-pill">
            Text {entry.direction === "in" ? "in" : "out"}
          </span>
        ) : auto ? (
          // v2.10 P2.4: an auto outcome is a dotted pill with the duration — "VM? 0:48", "Conn? 3:12".
          <span
            className={cn(
              "rounded-full border border-dotted px-1.5 text-[10px] font-medium whitespace-nowrap",
              outcomeClass(entry.outcome),
            )}
            data-testid="calllog-auto-pill"
            data-outcome={entry.outcome ?? ""}
            title={`Google Voice: ${formatClock(entry.duration_s)} on the line — the outcome is a guess until you confirm`}
          >
            {autoLabel(entry.outcome, entry.duration_s)}
          </span>
        ) : missed ? (
          <OutcomePill outcome={entry.outcome} testId="calllog-outcome">
            {entry.voice_kind === "voicemail" ? "Voicemail" : "Missed call"}
          </OutcomePill>
        ) : (
          <OutcomePill outcome={entry.outcome} testId="calllog-outcome">
            {entry.channel === "call" || entry.channel === "voicemail" ? undefined : entry.channel}
          </OutcomePill>
        )}
        {auto && editable && (
          <button
            type="button"
            className="inline-flex min-h-6 items-center rounded-full border border-green-500/40 px-1.5 text-[10px] font-semibold text-green-700 hover:bg-green-500/10 pointer-coarse:min-h-11 pointer-coarse:px-3 dark:text-green-300"
            data-testid="calllog-confirm"
            title="That is what happened — confirm it"
            onClick={() =>
              void run(() => callLog.update(entry.id, { confirm: true }), "Confirmed").then(() =>
                setNoteAfterConfirm(true),
              )
            }
          >
            ✓
          </button>
        )}
        {isText && entry.status && (
          <span
            className={cn(
              "rounded-full border px-1.5 text-[10px]",
              entry.status === "sent" && "border-green-500/40 text-green-700 dark:text-green-300",
              entry.status === "failed" && "border-danger/40 text-danger",
              (entry.status === "pending_manual" || entry.status === "unconfirmed") && "border-warn/40",
            )}
            data-testid="calllog-text-status"
            data-status={entry.status}
          >
            {TEXT_STATUS_LABEL[entry.status] ?? entry.status}
          </span>
        )}
        <span className="inline-flex items-center text-fg-muted" title={entry.direction === "in" ? "Inbound" : "Outbound"}>
          <Arrow className="size-3" aria-hidden />
          {entry.direction}
        </span>
        {entry.phone_e164 && (
          <PhoneLink e164={entry.phone_e164} compact leadId={entry.lead_id} dealId={entry.deal_id} surface="call-log" />
        )}
        {(entry.duration_s ?? 0) > 0 && !auto && (
          <span className="tabular-nums text-fg-muted" data-testid="calllog-duration">
            {formatDuration(entry.duration_s)}
          </span>
        )}
        {owner && entry.phone_e164 && missed && (
          <a
            href={voiceDialUrl(entry.phone_e164)}
            target={VOICE_WINDOW}
            className="rounded-full border border-accent/40 px-1.5 text-[10px] font-medium text-accent pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:items-center pointer-coarse:px-3"
            data-testid="calllog-call-back"
            onClick={(e) => {
              e.stopPropagation();
              recordDial(entry.phone_e164!, { leadId: entry.lead_id, dealId: entry.deal_id, surface: "call-back" });
            }}
          >
            Call back
          </a>
        )}
        {owner && entry.phone_e164 && isText && entry.direction === "in" && (
          <TextButton
            owner={owner}
            phones={[entry.phone_e164]}
            phone={entry.phone_e164}
            compact
            label="Reply"
            testId="calllog-reply"
            onSent={onChanged}
          />
        )}
        {entry.telephony_event_id && (
          <span className="rounded border px-1 text-[10px] text-fg-muted" title="Google Voice saw this call">
            Voice
          </span>
        )}
        {entry.from_note_id && <span className="text-[10px] text-fg-muted">from a note</span>}
        <span className="ml-auto">
          <ActionMenu
            testId="calllog-menu"
            items={[
              { label: "Edit note", testId: "calllog-edit", hidden: !editable, onSelect: () => setEditing(true) },
              { label: "Change outcome", testId: "calllog-change", hidden: !editable, onSelect: () => setChanging(true) },
              {
                label: "Copy phone",
                testId: "calllog-copy",
                hidden: !entry.phone_e164,
                onSelect: () => {
                  void navigator.clipboard?.writeText(entry.phone_e164 ?? "").catch(() => undefined);
                  toast.push("Phone copied");
                },
              },
              {
                label: "Move back to notes",
                testId: "calllog-unconvert",
                hidden: !entry.from_note_id || !editable,
                onSelect: () => void run(() => callLog.moveToNotes(entry.id), "Back in Notes"),
              },
              {
                label: "Delete",
                testId: "calllog-delete",
                danger: true,
                hidden: !editable,
                onSelect: () => {
                  callLog.remove(entry.id).then(
                    () => {
                      onChanged();
                      toast.push("Call deleted", "ok", {
                        durationMs: 5000,
                        action: {
                          label: "Undo",
                          onClick: () =>
                            void callLog.restore(entry.id).then(
                              () => {
                                toast.push("Call restored");
                                onChanged();
                              },
                              (e: Error) => toast.push(e.message, "danger"),
                            ),
                        },
                      });
                    },
                    (e: Error) => toast.push(e.message, "danger"),
                  );
                },
              },
            ]}
          />
        </span>
      </div>
      {auto && entry.provisional && editable && !changing && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="calllog-auto-chips">
          {OUTCOME_CHIPS.filter((o) => o !== entry.outcome).map((o) => (
            <button
              key={o}
              type="button"
              className={cn("rounded-full border px-1.5 text-[10px] pointer-coarse:min-h-11 pointer-coarse:px-3", outcomeClass(o))}
              data-testid={`calllog-auto-chip-${o}`}
              onClick={() =>
                void run(() => callLog.update(entry.id, { outcome: o }), `Outcome: ${OUTCOME_LABEL[o]}`).then(() =>
                  setNoteAfterConfirm(true),
                )
              }
            >
              {OUTCOME_SHORT[o]}
            </button>
          ))}
        </div>
      )}
      {isText && entry.status === "unconfirmed" && editable && (
        <div className="mt-1 flex items-center gap-1.5" data-testid="calllog-did-send">
          <span>Did this send?</span>
          <Button size="sm" onClick={() => void run(() => sms.didSend(entry.id, true), "Marked sent")} data-testid="calllog-did-send-yes">
            Yes
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void run(() => sms.didSend(entry.id, false), "Left as not sent")} data-testid="calllog-did-send-no">
            No
          </Button>
        </div>
      )}
      {isText && entry.body && (
        <p className="mt-0.5 whitespace-pre-wrap break-words" data-testid="calllog-text-body">
          {entry.body}
        </p>
      )}
      {noteAfterConfirm && editable && (
        <div className="mt-1">
          <NoteComposer
            testId="calllog-confirm-note"
            value={draft}
            onChange={setDraft}
            autoFocus
            submitLabel="Save"
            placeholder="A note on that call (optional)…"
            onCancel={() => setNoteAfterConfirm(false)}
            onSubmit={() =>
              run(() => callLog.update(entry.id, { note: draft }), "Note saved").then(() => setNoteAfterConfirm(false))
            }
          />
        </div>
      )}
      {changing && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="calllog-change-chips">
          {OUTCOME_CHIPS.map((o) => (
            <button
              key={o}
              type="button"
              className={cn("rounded-full border px-1.5 text-[10px]", outcomeClass(o), o === entry.outcome && "ring-1 ring-accent")}
              data-testid={`calllog-change-${o}`}
              onClick={() => {
                setChanging(false);
                void run(() => callLog.update(entry.id, { outcome: o }), `Outcome: ${OUTCOME_LABEL[o]}`);
              }}
            >
              {OUTCOME_LABEL[o]}
            </button>
          ))}
          <button type="button" className="text-[10px] text-fg-muted underline" onClick={() => setChanging(false)}>
            cancel
          </button>
        </div>
      )}
      {editing ? (
        <div className="mt-1">
          <NoteComposer
            testId="calllog-edit-note"
            ids={{ body: "calllog-editor" }}
            value={draft}
            onChange={setDraft}
            autoFocus
            submitLabel="Save"
            onCancel={() => {
              setDraft(entry.note ?? "");
              setEditing(false);
            }}
            onSubmit={() => {
              setEditing(false);
              return run(() => callLog.update(entry.id, { note: draft }), "Note saved");
            }}
          />
        </div>
      ) : (
        entry.note && <p className="mt-0.5 whitespace-pre-wrap break-words" data-testid="calllog-note-text">{entry.note}</p>
      )}
    </li>
  );
}

/** The panel: quick log on top, entries under it. */
export function CallLogPanel({
  owner,
  entries,
  phones,
  canEdit,
  onChanged,
  focusRef,
}: {
  owner: CallOwner;
  entries: readonly CallLogEntry[];
  phones: readonly string[];
  canEdit: (entry: CallLogEntry) => boolean;
  onChanged: () => void;
  focusRef?: React.MutableRefObject<(() => void) | null>;
}) {
  return (
    <div className="space-y-2" data-testid="calllog-panel">
      <QuickLog owner={owner} phones={phones} focusRef={focusRef} onLogged={() => onChanged()} />
      <CallLogEntries entries={entries} canEdit={canEdit} onChanged={onChanged} />
      <p className="text-[10px] text-fg-muted">
        L opens this row from anywhere on the page. Nothing here dials or sends — log what happened.
      </p>
    </div>
  );
}
